/**
 * @file ProductKindForm.tsx
 * @description Создание и правка вида товара (phase19.22, W22, макет §4).
 *
 * Правая шторка: на телефоне во всю ширину (фактически fullScreen), на ≥sm —
 * 560px. Поля — `ProductKindFormFields`, здесь — загрузка значения, проверка,
 * сохранение и разбор ошибок сервера: код из реестра `KIND_ERROR_FIELD` уходит
 * в поле, остальное — общей строкой и тостом.
 *
 * Подпись «Изменения применятся к N товарам без собственной настройки» — число
 * товаров с этим действующим видом (свой шаблон/срок у фасовки/товара
 * перекрывает вид, поэтому «без собственной настройки»).
 */

import { Info } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { ApiError } from "../../../lib/api-client";
import { productKindsApi, type ProductKind } from "../../../lib/api-product-kinds";
import { pluralRu } from "../../../lib/plural";
import {
  KIND_ERROR_FIELD,
  KIND_ERROR_TEXT,
  kindFormFromRecord,
  kindFormToInput,
  validateKindForm,
  type ProductKindFormErrors,
  type ProductKindFormValue,
} from "./product-kind-form";
import { ProductKindFormFields } from "./ProductKindFormFields";

interface Props {
  open: boolean;
  /** null — создание нового вида. */
  kind: ProductKind | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ProductKindForm({ open, kind, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [value, setValue] = useState<ProductKindFormValue>(() => kindFormFromRecord(kind));
  const [errors, setErrors] = useState<ProductKindFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Открыли другой вид — форма заново из записи.
  useEffect(() => {
    setValue(kindFormFromRecord(kind));
    setErrors({});
    setFormError(null);
  }, [kind, open]);

  function patch(next: Partial<ProductKindFormValue>) {
    setValue((v) => ({ ...v, ...next }));
    // Исправил поле — снимаем его ошибку, остальные оставляем.
    setErrors((e) => {
      const copy = { ...e };
      for (const key of Object.keys(next) as Array<keyof ProductKindFormValue>) delete copy[key];
      return copy;
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const found = validateKindForm(value);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      const input = kindFormToInput(value);
      if (kind) await productKindsApi.update(kind.id, input);
      else await productKindsApi.create(input);
      notify.success(kind ? "Вид товара сохранён" : "Вид товара создан");
      onSaved();
    } catch (err) {
      const field = err instanceof ApiError ? KIND_ERROR_FIELD[err.code] : undefined;
      if (err instanceof ApiError && field) {
        setErrors((prev) => ({ ...prev, [field]: KIND_ERROR_TEXT[err.code] ?? err.message }));
      } else {
        const msg = err instanceof ApiError ? err.message : "Не удалось сохранить вид товара";
        setFormError(msg);
        notify.error(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  const affected = kind?.productsCount ?? 0;

  return (
    <Sheet open={open} onOpenChange={(o) => (!o && !saving ? onClose() : null)}>
      <SheetContent
        side="right"
        className="w-full max-w-none gap-0 overflow-y-auto p-0 sm:max-w-[560px]"
      >
        <SheetHeader className="px-4 pt-5 pb-3 sm:px-6">
          <SheetTitle>{kind ? `Вид «${kind.name}»` : "Новый вид товара"}</SheetTitle>
          <SheetDescription>
            Срок годности, этикетка и НДС для товаров этого вида.
          </SheetDescription>
        </SheetHeader>

        <form
          id="product-kind-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-1 flex-col gap-4 px-4 pb-4 sm:px-6"
        >
          <ProductKindFormFields
            value={value}
            errors={errors}
            onChange={patch}
            disabled={saving}
          />
          {kind ? (
            <p className="flex items-start gap-2 text-xs text-on-surface-variant">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              Изменения применятся к {affected}{" "}
              {pluralRu(affected, ["товару", "товарам", "товарам"])} без собственной настройки.
            </p>
          ) : null}
          {formError ? <Alert severity="error">{formError}</Alert> : null}
        </form>

        {/* Кнопки прилипают к низу шторки: на 360 форма длиннее экрана. */}
        <SheetFooter className="sticky bottom-0 bg-surface-lowest px-4 py-3 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            className="min-h-12"
            onClick={onClose}
            disabled={saving}
          >
            Отмена
          </Button>
          <Button type="submit" form="product-kind-form" className="min-h-12" disabled={saving}>
            {saving ? "Сохраняем…" : kind ? "Сохранить" : "Создать"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
